import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Volume2,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Sparkles,
  ChevronDown,
  X,
  Mail,
  CalendarDays,
} from "lucide-react";
import { useSiteContext } from "../../../context/SiteContext";
import { createScheduledMeeting, getMeetingRooms } from "../../../services/roomServices";
import userService from "../../../services/userServices";
import { toast } from "react-toastify";
import Calendar from "../../calendar";
import "../../calendar/Calendar.css";
import { TIMEZONES } from "../../../utils/timezones";

import { Constants, MeetingProvider } from "../../../config/sdkConfig";
import { MeetingAppProvider } from "../../../MeetingAppContextDef";
import { MeetingContainerNew } from "../../../meeting/MeetingContainerNew";
import { LeaveScreenNew } from "../../screens/LeaveScreenNew";
import WaitingToJoinScreenNew from "../../screens/WaitingToJoinScreenNew";
import * as meetingServices from "../../../services/meetingServices";

// Helper function to format datetime to mm/dd/yyyy hh:mm AM/PM for display
const formatDateTimeForDisplay = (datetimeString, timezone = null) => {
  if (!datetimeString) return "";

  const date = new Date(datetimeString);

  // Extract timezone identifier if provided
  let timezoneIdentifier = null;
  if (timezone) {
    if (typeof timezone === "string") {
      timezoneIdentifier = timezone.split("(")[0].trim();
    } else {
      timezoneIdentifier = timezone;
    }
  }

  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(timezoneIdentifier && { timeZone: timezoneIdentifier }),
  });

  const parts = formatter.formatToParts(date);
  const month = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  const year = parts.find((p) => p.type === "year").value;
  const hour = parts.find((p) => p.type === "hour").value;
  const minute = parts.find((p) => p.type === "minute").value;
  const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value || "";

  return `${month}/${day}/${year} ${hour}:${minute} ${dayPeriod.toUpperCase()}`;
};

const MeetingSetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, meetingSettings, setMeetingSettings, setMeetingRole, setWebinarMode, setCustomMeetingName } = useSiteContext();
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const [stream, setStream] = useState(null);
  const [displayName, setDisplayName] = useState(user?.name || "");
  // Default meeting name: "Meet Now" + current date (M/D/YYYY format)
  const getDefaultMeetingName = () => {
    const today = new Date();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    const year = today.getFullYear();
    return `Meet Now ${month}/${day}/${year}`;
  };
  const [eventName, setEventName] = useState(getDefaultMeetingName());
  const [audioLevel, setAudioLevel] = useState(0);
  const [isCreating, setIsCreating] = useState(false);

  const [permissions, setPermissions] = useState({
    camera: "pending",
    microphone: "pending",
  });

  const [devices, setDevices] = useState({
    cameras: [],
    microphones: [],
    speakers: [],
  });

  const [selectedDevices, setSelectedDevices] = useState({
    camera: "",
    microphone: "",
    speaker: "",
  });

  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);

  const [showCameraDropdown, setShowCameraDropdown] = useState(false);
  const [showMicDropdown, setShowMicDropdown] = useState(false);
  const [showSpeakerDropdown, setShowSpeakerDropdown] = useState(false);

  // Inline meeting state
  const [isMeetingStarted, setIsMeetingStarted] = useState(false);
  const [isMeetingLeft, setIsMeetingLeft] = useState(false);
  const [inlineMeetingId, setInlineMeetingId] = useState("");
  const [joiningToken, setJoiningToken] = useState(null);
  // Note: meetingSettings comes from useSiteContext, not local state
  const [customAudioStream, setCustomAudioStream] = useState(null);
  const [customVideoStream, setCustomVideoStream] = useState(null);
  const [selectedRole, setSelectedRole] = useState("moderator");
  const [isStartingInline, setIsStartingInline] = useState(false);
  const [isStartingMeetNow, setIsStartingMeetNow] = useState(false);
  const [showIntroVideo, setShowIntroVideo] = useState(false);

  const sessionType = searchParams.get("type") || "meeting";

  const sessionLabels = {
    meeting: "Meeting",
    webinar: "Webinar",
    livestream: "Live Stream",
  };

  // Scheduling form states
  const [startDate, setStartDate] = useState("");
  const [startHour, setStartHour] = useState("1");
  const [startMinute, setStartMinute] = useState("30");
  const [startAMPM, setStartAMPM] = useState("AM");
  const [duration, setDuration] = useState("60");
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [startDateObj, setStartDateObj] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  // Co-Host states
  const [invitesEmailInput, setInvitesEmailInput] = useState("");
  const [invitesList, setInvitesList] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState([]);
  const [checkingUser, setCheckingUser] = useState(false);
  const [userExists, setUserExists] = useState(null);
  const [inviteEmails, setInviteEmails] = useState([]);

  // Speakers states
  const [speakersEmailInput, setSpeakersEmailInput] = useState("");
  const [speakersList, setSpeakersList] = useState([]);
  const [showSpeakerSuggestions, setShowSpeakerSuggestions] = useState(false);
  const [selectedSpeakerSuggestionIndex, setSelectedSpeakerSuggestionIndex] =
    useState(-1);
  const [speakerSuggestions, setSpeakerSuggestions] = useState([]);
  const [checkingSpeakerUser, setCheckingSpeakerUser] = useState(false);
  const [speakerUserExists, setSpeakerUserExists] = useState(null);
  const [speakerInviteEmails, setSpeakerInviteEmails] = useState([]);

  // Room settings
  const [roomSettings, setRoomSettings] = useState({
    allowChat: true,
    allowScreenSharing: true,
    muteParticipantsOnEntry: false,
    allowReactions: true,
    allowRaiseHand: true,
    requirePassword: false,
    password: "",
    hostApprovalRequired: false,
    saveAsPreset: false,
    presetName: "",
  });

  // Business plan features
  const [exitUrlEnabled, setExitUrlEnabled] = useState(false);
  const [exitUrl, setExitUrl] = useState("");
  const [customButtonEnabled, setCustomButtonEnabled] = useState(false);
  const [customButtonUrl, setCustomButtonUrl] = useState("");
  const [customButtonText, setCustomButtonText] = useState("");
  const [waitingRoomTextEnabled, setWaitingRoomTextEnabled] = useState(false);
  const [waitingRoomTitle, setWaitingRoomTitle] = useState("");
  const [waitingRoomMessage, setWaitingRoomMessage] = useState("");
  const [waitingRoomSubText, setWaitingRoomSubText] = useState("");

  // YouTube streaming credentials (Live Stream only)
  const [streamYouTubeUrl, setStreamYouTubeUrl] = useState("rtmp://a.rtmp.youtube.com/live2");
  const [streamYouTubeKey, setStreamYouTubeKey] = useState("");

  // Room association
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("");

  // Custom background
  const [customBackgroundEnabled, setCustomBackgroundEnabled] = useState(false);
  const [backgroundType, setBackgroundType] = useState("solid");
  const [backgroundColor, setBackgroundColor] = useState("#1a1a2e");
  const [gradientColor1, setGradientColor1] = useState("#1a1a2e");
  const [gradientColor2, setGradientColor2] = useState("#615fff");
  const [gradientDirection, setGradientDirection] = useState("to right");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState(null);

  const timezones = TIMEZONES;
  const commonDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
    "aol.com",
    "mail.com",
    "protonmail.com",
  ];

  // Calculate durationHours and durationMinutes from duration
  const parsedDuration = parseInt(duration, 10);
  const normalizedDuration = Number.isNaN(parsedDuration) ? 0 : parsedDuration;
  const durationHours = Math.floor(normalizedDuration / 60);
  const durationMinutes = normalizedDuration % 60;

  // Time options for dropdown (15-minute intervals)
  const TIME_OPTIONS = (() => {
    const minutes = ["00", "15", "30", "45"];
    const options = [];
    for (let hour = 1; hour <= 12; hour += 1) {
      minutes.forEach((minute) => {
        options.push({
          value: `${hour}-${minute}`,
          label: `${hour}:${minute}`,
        });
      });
    }
    return options;
  })();

  // Helper functions
  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatDisplayDate = (dateValue) => {
    const dateToFormat =
      startDateObj ||
      (dateValue instanceof Date ? dateValue : parseDateFromInput(dateValue));
    if (!dateToFormat || isNaN(dateToFormat.getTime())) return "";
    return dateToFormat.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const parseDateFromInput = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split("/");
    if (parts.length !== 3) return null;
    const month = parseInt(parts[0], 10) - 1;
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  const isValidUrl = (urlString) => {
    try {
      const url = new URL(urlString);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  const handleSettingChange = (setting, value) => {
    setRoomSettings((prev) => ({ ...prev, [setting]: value }));
  };

  // Initialize date
  useEffect(() => {
    if (!isInitialized) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setStartDateObj(today);
      setStartDate(formatDateForInput(today));
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // Fetch user's rooms for room association
  useEffect(() => {
    if (sessionType === "meeting") {
      getMeetingRooms(1, 100, true).then((res) => {
        const fetchedRooms = res.data || [];
        setRooms(fetchedRooms);
        // Auto-select first room if none selected
        if (fetchedRooms.length > 0 && !selectedRoomId) {
          setSelectedRoomId(fetchedRooms[0].id || fetchedRooms[0]._id);
        }
      }).catch(() => {});
    }
  }, [sessionType]);

  // Keep streamRef in sync so the unmount cleanup can access the latest stream
  useEffect(() => {
    streamRef.current = stream;
  }, [stream]);

  // Media initialization
  useEffect(() => {
    initializeMedia();
    return () => {
      // Use ref instead of state — the [] dependency array captures stream as
      // null from mount time, so it would never actually stop tracks.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        console.log('[MeetingSetup] Unmount: stopped all media tracks');
      }
    };
  }, []);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  useEffect(() => {
    if (!stream || !isMicOn) {
      setAudioLevel(0);
      return;
    }

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(stream);
    microphone.connect(analyser);
    analyser.fftSize = 256;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let animationId;

    const updateLevel = () => {
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(Math.min(100, average * 2));
      animationId = requestAnimationFrame(updateLevel);
    };

    updateLevel();

    return () => {
      cancelAnimationFrame(animationId);
      audioContext.close();
    };
  }, [stream, isMicOn]);

  // Co-Host autocomplete
  useEffect(() => {
    const input = invitesEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!invitesList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!invitesList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!invitesList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    setSuggestions(newSuggestions.slice(0, 5));
    setShowSuggestions(newSuggestions.length > 0);
    setSelectedSuggestionIndex(-1);
  }, [invitesEmailInput, invitesList]);

  // Speaker autocomplete
  useEffect(() => {
    const input = speakersEmailInput.trim().toLowerCase();

    if (input.length === 0) {
      setSpeakerSuggestions([]);
      setShowSpeakerSuggestions(false);
      return;
    }

    const newSuggestions = [];
    const hasAtSymbol = input.includes("@");
    const parts = input.split("@");

    if (hasAtSymbol && parts.length === 2) {
      const [localPart, domainPart] = parts;

      if (domainPart.length > 0) {
        commonDomains.forEach((domain) => {
          if (domain.startsWith(domainPart) && domain !== domainPart) {
            const suggestion = `${localPart}@${domain}`;
            if (!speakersList.includes(suggestion)) {
              newSuggestions.push(suggestion);
            }
          }
        });
      } else {
        commonDomains.forEach((domain) => {
          const suggestion = `${localPart}@${domain}`;
          if (!speakersList.includes(suggestion)) {
            newSuggestions.push(suggestion);
          }
        });
      }
    } else if (!hasAtSymbol) {
      commonDomains.forEach((domain) => {
        const suggestion = `${input}@${domain}`;
        if (!speakersList.includes(suggestion)) {
          newSuggestions.push(suggestion);
        }
      });
    }

    setSpeakerSuggestions(newSuggestions.slice(0, 5));
    setShowSpeakerSuggestions(newSuggestions.length > 0);
    setSelectedSpeakerSuggestionIndex(-1);
  }, [speakersEmailInput, speakersList]);

  // Check if co-host user exists
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = invitesEmailInput.trim();

    if (input.length === 0) {
      setUserExists(null);
      setCheckingUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setUserExists(exists);
        } catch (error) {
          if (
            error.response?.status === 404 ||
            error.response?.data?.message?.toLowerCase().includes("not found")
          ) {
            setUserExists(false);
          } else {
            setUserExists(false);
            console.error("Error checking user:", error);
          }
        } finally {
          setCheckingUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setUserExists(null);
    }
  }, [invitesEmailInput]);

  // Check if speaker user exists
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const input = speakersEmailInput.trim();

    if (input.length === 0) {
      setSpeakerUserExists(null);
      setCheckingSpeakerUser(false);
      return;
    }

    if (emailRegex.test(input)) {
      const timeoutId = setTimeout(async () => {
        setCheckingSpeakerUser(true);
        try {
          const response = await userService.checkUserExists(input);
          const exists = response?.data?.exists ?? response?.exists ?? false;
          setSpeakerUserExists(exists);
        } catch (error) {
          if (
            error.response?.status === 404 ||
            error.response?.data?.message?.toLowerCase().includes("not found")
          ) {
            setSpeakerUserExists(false);
          } else {
            setSpeakerUserExists(false);
            console.error("Error checking speaker user:", error);
          }
        } finally {
          setCheckingSpeakerUser(false);
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setSpeakerUserExists(null);
    }
  }, [speakersEmailInput]);

  const initializeMedia = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      setStream(mediaStream);
      setPermissions({
        camera: "granted",
        microphone: "granted",
      });

      const deviceList = await navigator.mediaDevices.enumerateDevices();

      setDevices({
        cameras: deviceList.filter((d) => d.kind === "videoinput"),
        microphones: deviceList.filter((d) => d.kind === "audioinput"),
        speakers: deviceList.filter((d) => d.kind === "audiooutput"),
      });

      const videoTrack = mediaStream.getVideoTracks()[0];
      const audioTrack = mediaStream.getAudioTracks()[0];

      // Load saved device preferences from localStorage
      const savedCamera = localStorage.getItem('rlink_selectedCamera');
      const savedMic = localStorage.getItem('rlink_selectedMic');
      const savedSpeaker = localStorage.getItem('rlink_selectedSpeaker');

      const cameras = deviceList.filter((d) => d.kind === "videoinput");
      const microphones = deviceList.filter((d) => d.kind === "audioinput");
      const speakers = deviceList.filter((d) => d.kind === "audiooutput");

      // Use saved device if it exists, otherwise use current track or first available
      const cameraId = (savedCamera && cameras.some(c => c.deviceId === savedCamera))
        ? savedCamera
        : (videoTrack?.getSettings()?.deviceId || cameras[0]?.deviceId || "");
      const micId = (savedMic && microphones.some(m => m.deviceId === savedMic))
        ? savedMic
        : (audioTrack?.getSettings()?.deviceId || microphones[0]?.deviceId || "");
      const speakerId = (savedSpeaker && speakers.some(s => s.deviceId === savedSpeaker))
        ? savedSpeaker
        : (speakers[0]?.deviceId || "");

      setSelectedDevices({
        camera: cameraId,
        microphone: micId,
        speaker: speakerId,
      });
    } catch (error) {
      console.error("Media access error:", error);
      if (error.name === "NotAllowedError") {
        setPermissions({
          camera: "denied",
          microphone: "denied",
        });
      }
    }
  };

  const toggleCamera = () => {
    if (stream) {
      stream.getVideoTracks().forEach((track) => {
        track.enabled = !isCameraOn;
      });
      setIsCameraOn(!isCameraOn);
    }
  };

  const toggleMic = () => {
    if (stream) {
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !isMicOn;
      });
      setIsMicOn(!isMicOn);
    }
  };

  const handleAddCoHost = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    if (isInvite === null) {
      try {
        setCheckingUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        if (
          error.response?.status === 404 ||
          error.response?.data?.message?.toLowerCase().includes("not found")
        ) {
          isInvite = true;
        } else {
          isInvite = true;
          console.error("Error checking user:", error);
        }
      } finally {
        setCheckingUser(false);
      }
    }

    if (!invitesList.includes(trimmedEmail)) {
      setInvitesList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setInvitesEmailInput("");
      setShowSuggestions(false);
      setUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Co-host added");
    } else {
      toast.warning("Email already added.");
    }
  };

  const handleAddSpeaker = async (email, isInvite = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;

    if (isInvite === null) {
      try {
        setCheckingSpeakerUser(true);
        const response = await userService.checkUserExists(trimmedEmail);
        const exists = response?.data?.exists ?? response?.exists ?? false;
        isInvite = !exists;
      } catch (error) {
        if (
          error.response?.status === 404 ||
          error.response?.data?.message?.toLowerCase().includes("not found")
        ) {
          isInvite = true;
        } else {
          isInvite = true;
          console.error("Error checking speaker user:", error);
        }
      } finally {
        setCheckingSpeakerUser(false);
      }
    }

    if (!speakersList.includes(trimmedEmail)) {
      setSpeakersList((prev) => [...prev, trimmedEmail]);
      if (isInvite) {
        setSpeakerInviteEmails((prev) => [...prev, trimmedEmail]);
      }
      setSpeakersEmailInput("");
      setShowSpeakerSuggestions(false);
      setSpeakerUserExists(null);
      toast.success(isInvite ? "Email invite added" : "Speaker added");
    } else {
      toast.warning("Email already added.");
    }
  };

  const handleStartMeeting = async (meetNow = true) => {
    if (isCreating) return;

    // Validation
    if (!eventName.trim()) {
      toast.error("Please enter an event name");
      return;
    }

    if (eventName.trim().length < 3) {
      toast.error("Event name must be at least 3 characters");
      return;
    }

    // Room is required for meetings
    if (sessionType === "meeting" && rooms.length > 0 && !selectedRoomId) {
      toast.error("Please select a room");
      return;
    }

    // Validate Business plan features
    if (user?.plan === "Business") {
      if (exitUrlEnabled && (!exitUrl || !isValidUrl(exitUrl))) {
        toast.error("Please enter a valid exit URL");
        return;
      }

      if (
        customButtonEnabled &&
        (!customButtonText.trim() || customButtonText.trim().length < 3)
      ) {
        toast.error("Custom button text must be at least 3 characters");
        return;
      }

      if (
        customButtonEnabled &&
        (!customButtonUrl || !isValidUrl(customButtonUrl))
      ) {
        toast.error("Please enter a valid custom button URL");
        return;
      }

      if (waitingRoomTextEnabled) {
        if (!waitingRoomTitle.trim() || waitingRoomTitle.trim().length < 3) {
          toast.error("Waiting room title must be at least 3 characters");
          return;
        }
        if (
          !waitingRoomMessage.trim() ||
          waitingRoomMessage.trim().length < 3
        ) {
          toast.error("Waiting room message must be at least 3 characters");
          return;
        }
        if (
          !waitingRoomSubText.trim() ||
          waitingRoomSubText.trim().length < 3
        ) {
          toast.error("Waiting room sub text must be at least 3 characters");
          return;
        }
      }
    }

    // Validate background image size
    if (
      customBackgroundEnabled &&
      backgroundType === "image" &&
      backgroundImage
    ) {
      const maxSize = 5 * 1024 * 1024;
      if (backgroundImage.size > maxSize) {
        toast.error("Background image size should not exceed 5MB");
        return;
      }
    }

    setIsCreating(true);

    try {
      // Calculate start and end times
      let startDateTime;
      if (meetNow) {
        startDateTime = new Date();
      } else {
        const dateObj = new Date(startDateObj);
        let hours24 = parseInt(startHour, 10);
        if (startAMPM === "PM" && hours24 !== 12) {
          hours24 += 12;
        } else if (startAMPM === "AM" && hours24 === 12) {
          hours24 = 0;
        }
        const minutes = parseInt(startMinute, 10) || 0;
        dateObj.setHours(hours24, minutes, 0, 0);
        startDateTime = dateObj;
      }

      const durationMinutes = parseInt(duration || "60", 10);
      const endDateTime = new Date(startDateTime);
      endDateTime.setMinutes(endDateTime.getMinutes() + durationMinutes);

      const roomType =
        sessionType === "webinar"
          ? "Webinar"
          : sessionType === "livestream"
          ? "Live Stream"
          : "Meeting";

      const formData = new FormData();
      formData.append("roomName", eventName.trim());
      formData.append("roomType", roomType);
      formData.append("startTime", startDateTime.toISOString());
      formData.append("endTime", endDateTime.toISOString());
      formData.append("timezone", selectedTimezone);

      // Add co-hosts and speakers
      if (invitesList.length > 0) {
        formData.append("coHosts", JSON.stringify(invitesList));
      }
      if (speakersList.length > 0) {
        formData.append("speakers", JSON.stringify(speakersList));
      }

      // Add room settings
      formData.append("allowChat", roomSettings.allowChat);
      formData.append("allowScreenSharing", roomSettings.allowScreenSharing);
      formData.append(
        "muteParticipantsOnEntry",
        roomSettings.muteParticipantsOnEntry
      );
      formData.append("allowReactions", roomSettings.allowReactions);
      formData.append("allowRaiseHand", roomSettings.allowRaiseHand);
      formData.append("requirePassword", roomSettings.requirePassword);
      if (roomSettings.requirePassword && roomSettings.password) {
        formData.append("password", roomSettings.password);
      }
      formData.append(
        "hostApprovalRequired",
        roomSettings.hostApprovalRequired
      );

      // Business plan features
      if (user?.plan === "Business") {
        if (exitUrlEnabled && exitUrl) {
          formData.append("exitUrl", exitUrl);
        }
        if (customButtonEnabled && customButtonText && customButtonUrl) {
          formData.append("customButtonText", customButtonText);
          formData.append("customButtonUrl", customButtonUrl);
        }
        if (
          waitingRoomTextEnabled &&
          waitingRoomTitle &&
          waitingRoomMessage &&
          waitingRoomSubText
        ) {
          formData.append("waitingRoomTitle", waitingRoomTitle);
          formData.append("waitingRoomMessage", waitingRoomMessage);
          formData.append("waitingRoomSubText", waitingRoomSubText);
        }
      }

      // Custom background
      if (customBackgroundEnabled) {
        formData.append("customBackgroundEnabled", true);
        formData.append("backgroundType", backgroundType);
        if (backgroundType === "solid") {
          formData.append("backgroundColor", backgroundColor);
        } else if (backgroundType === "gradient") {
          formData.append("gradientColor1", gradientColor1);
          formData.append("gradientColor2", gradientColor2);
          formData.append("gradientDirection", gradientDirection);
        } else if (backgroundType === "image" && backgroundImage) {
          formData.append("backgroundImage", backgroundImage);
        }
      }

      // Save as preset
      if (roomSettings.saveAsPreset && roomSettings.presetName) {
        formData.append("saveAsPreset", true);
        formData.append("presetName", roomSettings.presetName);
      }

      // Include YouTube streaming credentials for Live Stream sessions
      if (sessionType === "livestream" && streamYouTubeKey) {
        formData.append("streamYouTubeUrl", streamYouTubeUrl);
        formData.append("streamYouTubeKey", streamYouTubeKey);
      }

      const response = await createScheduledMeeting(formData);

      if (response.data?.roomId) {
        // Stop the local stream before starting inline meeting
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }

        // Store settings for the meeting
        localStorage.setItem("meetingMicOn", isMicOn);
        localStorage.setItem("meetingWebcamOn", isCameraOn);
        localStorage.setItem("displayName", displayName || "Guest");

        if (meetNow) {
          // Navigate to the meeting room immediately (original behavior)
          window.location.href = `/${response.data.roomId}`;
        } else {
          // Navigate back to dashboard with success message
          toast.success("Meeting scheduled successfully!");
          navigate("/dashboard/rooms");
        }
      } else {
        throw new Error("Failed to create room");
      }
    } catch (error) {
      console.error("Error creating meeting:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to create meeting. Please try again."
      );
      setIsCreating(false);
    }
  };

  const getPermissionIcon = (status) => {
    switch (status) {
      case "granted":
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case "denied":
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      default:
        return (
          <div className="w-5 h-5 rounded-full border-2 border-gray-500 border-t-white animate-spin" />
        );
    }
  };

  const handleCameraChange = async (deviceId) => {
    setSelectedDevices({ ...selectedDevices, camera: deviceId });
    setShowCameraDropdown(false);
    // Save to localStorage
    localStorage.setItem('rlink_selectedCamera', deviceId);

    if (stream) {
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId } },
          audio: { deviceId: { exact: selectedDevices.microphone } },
        });

        stream.getTracks().forEach((track) => track.stop());
        setStream(newStream);
      } catch (error) {
        console.error("Error switching camera:", error);
      }
    }
  };

  const handleMicChange = async (deviceId) => {
    setSelectedDevices({ ...selectedDevices, microphone: deviceId });
    setShowMicDropdown(false);
    // Save to localStorage
    localStorage.setItem('rlink_selectedMic', deviceId);

    if (stream) {
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: selectedDevices.camera } },
          audio: { deviceId: { exact: deviceId } },
        });

        stream.getTracks().forEach((track) => track.stop());
        setStream(newStream);
      } catch (error) {
        console.error("Error switching microphone:", error);
      }
    }
  };

  const getSelectedCameraLabel = () => {
    const camera = devices.cameras.find(
      (d) => d.deviceId === selectedDevices.camera
    );
    return camera?.label || "Select Camera";
  };

  const getSelectedMicLabel = () => {
    const mic = devices.microphones.find(
      (d) => d.deviceId === selectedDevices.microphone
    );
    return mic?.label || "Select Microphone";
  };

  const handleSpeakerChange = (deviceId) => {
    setSelectedDevices({ ...selectedDevices, speaker: deviceId });
    setShowSpeakerDropdown(false);
    // Store selected speaker in localStorage for use in the meeting
    localStorage.setItem("rlink_selectedSpeaker", deviceId);
  };

  const getSelectedSpeakerLabel = () => {
    const speaker = devices.speakers.find(
      (d) => d.deviceId === selectedDevices.speaker
    );
    return speaker?.label || "Select Speaker";
  };

  // Handle meeting leave
  const handleMeetingLeave = () => {
    console.log("handleMeetingLeave called");
    setJoiningToken(null);
    setInlineMeetingId("");
    setIsMeetingStarted(false);
    setShowIntroVideo(false);
    // Reset to default instead of null to avoid issues
    setMeetingSettings({ guestCanUnmute: false });
  };

  // Start meeting inline (for "Start Meeting" button)
  const handleStartMeetingInline = async () => {
    if (isStartingInline) return;

    // Validation - only check event name for inline start
    if (!eventName.trim()) {
      toast.error("Please enter an event name");
      return;
    }

    if (eventName.trim().length < 3) {
      toast.error("Event name must be at least 3 characters");
      return;
    }

    // Room is required for meetings
    if (sessionType === "meeting" && rooms.length > 0 && !selectedRoomId) {
      toast.error("Please select a room");
      return;
    }

    setIsStartingInline(true);
    // Show intro video immediately when starting inline meeting
    setShowIntroVideo(true);

    try {
      // Create meeting with minimal settings for quick start
      const startDateTime = new Date();
      const durationMinutes = 60;
      // Format start time as ISO string for the backend
      const startTimeFormatted = startDateTime.toISOString();

      const roomType = sessionType === "webinar" ? "Webinar" : sessionType === "livestream" ? "Live Stream" : "Meeting";

      const formData = new FormData();
      formData.append("name", eventName);
      formData.append("isNow", "true");
      formData.append("roomType", roomType);
      formData.append("startTime", startTimeFormatted);
      formData.append("duration", durationMinutes.toString());
      formData.append("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);

      // Associate with existing room for Meetings
      if (sessionType === "meeting" && selectedRoomId) {
        formData.append("associatedRoomId", selectedRoomId);
      }

      // Include YouTube streaming credentials for Live Stream sessions
      if (sessionType === "livestream" && streamYouTubeKey) {
        formData.append("streamYouTubeUrl", streamYouTubeUrl);
        formData.append("streamYouTubeKey", streamYouTubeKey);
      }

      const response = await createScheduledMeeting(formData);

      if (response.data?.roomId) {
        // Stop the local stream
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }

        // Store settings
        localStorage.setItem("meetingMicOn", isMicOn);
        localStorage.setItem("meetingWebcamOn", isCameraOn);
        localStorage.setItem("displayName", displayName || "Guest");

        // Get meeting details and joining token for inline meeting
        let lookupSlug = response.data.roomId;
        if (selectedRoomId) {
          const selectedRoom = rooms.find(r => (r.id || r._id) === selectedRoomId);
          if (selectedRoom) {
            lookupSlug = selectedRoom.roomPermalink || selectedRoom.roomName || selectedRoom.roomId || lookupSlug;
          }
        }
        console.log("Created meeting, roomId:", lookupSlug);
        const meetingResponse = await meetingServices.getMeetingByName(lookupSlug);
        console.log("Meeting response:", meetingResponse);
        const meeting = meetingResponse.meeting;

        if (meeting) {
          console.log("Meeting details:", meeting);
          // Set meeting settings and role in SiteContext (required for MeetingContainerNew)
          // Ensure isNow is true and roomType is correctly set for the meeting container
          const meetingWithSettings = {
            ...meeting,
            isNow: true,
            roomType: meeting.roomType || "Meeting",
          };
          setMeetingSettings(meetingWithSettings);
          setMeetingRole(selectedRole);
          // Set the custom meeting name from eventName
          setCustomMeetingName(eventName);
          // Explicitly set webinarMode to avoid race conditions with context useEffect
          setWebinarMode(Constants.modes.SEND_AND_RECV);
          setInlineMeetingId(meeting.roomId);

          // Get joining token
          const tokenResponse = await meetingServices.getJoiningToken({ roomId: meeting.roomId });
          console.log("Token response:", tokenResponse);
          if (tokenResponse.token) {
            console.log("Setting meeting state - token received");
            setJoiningToken(tokenResponse.token);
            setIsMeetingStarted(true);
            setIsStartingInline(false);
            // Hide intro video once meeting is ready
            setShowIntroVideo(false);
            console.log("Meeting state set - should render portal now");
          } else {
            throw new Error("Failed to get joining token");
          }
        } else {
          throw new Error("Meeting not found");
        }
      } else {
        throw new Error("Failed to create room");
      }
    } catch (error) {
      console.error("Error creating inline meeting:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to create meeting. Please try again."
      );
      setIsStartingInline(false);
      setShowIntroVideo(false);
    }
  };

  // Handle Meet Now button (redirects to new page)
  const handleMeetNow = async () => {
    if (isStartingMeetNow) return;

    // Validation - only check event name
    if (!eventName.trim()) {
      toast.error("Please enter an event name");
      return;
    }

    if (eventName.trim().length < 3) {
      toast.error("Event name must be at least 3 characters");
      return;
    }

    setIsStartingMeetNow(true);

    try {
      // Create meeting with minimal settings for quick start
      const startDateTime = new Date();
      const durationMinutes = 60;
      // Format start time as ISO string for the backend
      const startTimeFormatted = startDateTime.toISOString();

      const roomType = sessionType === "webinar" ? "Webinar" : sessionType === "livestream" ? "Live Stream" : "Meeting";

      const formData = new FormData();
      formData.append("name", eventName);
      formData.append("isNow", "true");
      formData.append("roomType", roomType);
      formData.append("startTime", startTimeFormatted);
      formData.append("duration", durationMinutes.toString());
      formData.append("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);

      // Associate with existing room for Meetings
      if (sessionType === "meeting" && selectedRoomId) {
        formData.append("associatedRoomId", selectedRoomId);
      }

      // Include YouTube streaming credentials for Live Stream sessions
      if (sessionType === "livestream" && streamYouTubeKey) {
        formData.append("streamYouTubeUrl", streamYouTubeUrl);
        formData.append("streamYouTubeKey", streamYouTubeKey);
      }

      const response = await createScheduledMeeting(formData);

      if (response.data?.roomId) {
        // Stop the local stream
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }

        // Store settings
        localStorage.setItem("meetingMicOn", isMicOn);
        localStorage.setItem("meetingWebcamOn", isCameraOn);
        localStorage.setItem("displayName", displayName || "Guest");
        // Flag to skip pre-launch check for host
        localStorage.setItem("skipPrelaunchCheck", "true");
        // For Meetings only: tell the meeting page to honour the Display Name
        // chosen here instead of falling back to the authenticated user's name.
        // Consumed once by Conference.js and then cleared.
        if (sessionType === "meeting") {
          localStorage.setItem("rlink_useSetupDisplayName", "true");
        }

        // Navigate to the associated room's permalink if selected, otherwise the new roomId
        let navigateTo = response.data.roomId;
        if (selectedRoomId) {
          const selectedRoom = rooms.find(r => (r.id || r._id) === selectedRoomId);
          if (selectedRoom) {
            // Use roomPermalink (the URL slug). Fall back to roomName/roomId if missing.
            const permalink = selectedRoom.roomPermalink;
            const validPermalink = permalink && permalink !== "false" && permalink !== "true" ? permalink : null;
            navigateTo = validPermalink || selectedRoom.roomName || selectedRoom.roomId || navigateTo;
          }
        }
        window.location.href = `/${navigateTo}`;
      } else {
        throw new Error("Failed to create room");
      }
    } catch (error) {
      console.error("Error creating meeting:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to create meeting. Please try again."
      );
      setIsStartingMeetNow(false);
    }
  };

  // Show intro video when starting meeting (before meeting is fully ready)
  if (showIntroVideo && !isMeetingStarted) {
    const introContent = (
      <div
        id="inline-meeting-intro"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 999999,
          overflow: 'hidden',
          backgroundColor: '#000000'
        }}
      >
        <WaitingToJoinScreenNew visible={true} overlayType="video" />
      </div>
    );
    return ReactDOM.createPortal(introContent, document.body);
  }

  // If meeting is started, show the meeting container using Portal to render outside React tree
  if (isMeetingStarted && joiningToken && inlineMeetingId) {
    console.log("Rendering inline meeting portal", {
      isMeetingStarted,
      hasJoiningToken: !!joiningToken,
      inlineMeetingId,
      selectedRole,
      meetingSettings: meetingSettings
    });
    const meetingContent = (
      <div
        id="inline-meeting-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 999999,
          overflow: 'hidden',
          backgroundColor: '#001233'
        }}
      >
        <MeetingAppProvider>
          {isMeetingLeft ? (
            <LeaveScreenNew
              setIsMeetingLeft={setIsMeetingLeft}
              meetingId={inlineMeetingId}
            />
          ) : (
            <MeetingProvider
              config={{
                meetingId: inlineMeetingId,
                participantId: user?.id || localStorage.getItem(inlineMeetingId) || Math.random().toString(36).substring(2, 15),
                micEnabled: selectedRole === "moderator" ? isMicOn : false,
                webcamEnabled: selectedRole === "moderator" ? isCameraOn : false,
                name: displayName || "Guest",
                multiStream: true,
                metaData: {
                  profileImage: user?.logo,
                  meetingRole: selectedRole,
                  userId: user?.id,
                },
                permissions: {
                  removeParticipant: selectedRole === "moderator",
                },
                mode: selectedRole === "participant" ? Constants.modes.RECV_ONLY : Constants.modes.SEND_AND_RECV,
                translationLanguage: "en",
                speakingLanguage: "en",
              }}
              token={joiningToken}
              reinitialiseMeetingOnConfigChange={true}
              joinWithoutUserInteraction={true}
            >
              <MeetingContainerNew
                onMeetingLeave={handleMeetingLeave}
                setIsMeetingLeft={setIsMeetingLeft}
                setCustomVideoStream={setCustomVideoStream}
                micOn={isMicOn}
                setMicOn={setIsMicOn}
                webcamOn={isCameraOn}
                setWebcamOn={setIsCameraOn}
                customAudioStream={customAudioStream}
                setCustomAudioStream={setCustomAudioStream}
                customVideoStream={customVideoStream}
              />
            </MeetingProvider>
          )}
        </MeetingAppProvider>
      </div>
    );

    // Use Portal to render meeting container directly into body element
    return ReactDOM.createPortal(meetingContent, document.body);
  }

  return (
    <div className="min-h-screen bg-[#001233] text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col lg:flex-row">
        {/* Left Section - Video Preview */}
        <div className="flex-1 flex items-center justify-center p-4 md:p-6 lg:p-8">
          <div className="w-full max-w-2xl">
            <div className="relative aspect-video bg-gray-900 rounded-2xl overflow-hidden border border-white/10">
              {permissions.camera === "granted" && isCameraOn ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: "scaleX(-1)" }}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
                    <span className="text-3xl font-bold font-sans">
                      {displayName ? displayName[0].toUpperCase() : "G"}
                    </span>
                  </div>
                </div>
              )}

              {/* Controls Overlay */}
              <div className="absolute bottom-3 md:bottom-4 left-1/2 -translate-x-1/2 flex gap-2 md:gap-3">
                <button
                  onClick={toggleMic}
                  className={`p-3 md:p-4 rounded-full transition-all ${
                    isMicOn
                      ? "bg-white/10 hover:bg-white/20"
                      : "bg-red-500 hover:bg-red-600"
                  }`}
                >
                  {isMicOn ? (
                    <Mic className="w-4 h-4 md:w-5 md:h-5" />
                  ) : (
                    <MicOff className="w-4 h-4 md:w-5 md:h-5" />
                  )}
                </button>

                <button
                  onClick={toggleCamera}
                  className={`p-3 md:p-4 rounded-full transition-all ${
                    isCameraOn
                      ? "bg-white/10 hover:bg-white/20"
                      : "bg-red-500 hover:bg-red-600"
                  }`}
                >
                  {isCameraOn ? (
                    <Video className="w-4 h-4 md:w-5 md:h-5" />
                  ) : (
                    <VideoOff className="w-4 h-4 md:w-5 md:h-5" />
                  )}
                </button>
              </div>

              {/* Name Label */}
              <div className="absolute bottom-3 md:bottom-4 left-3 md:left-4 bg-black/50 backdrop-blur-sm px-2.5 py-1 md:px-3 md:py-1.5 rounded-lg text-xs md:text-sm font-sans truncate max-w-[calc(50%-3.5rem)]">
                {displayName || "Guest"}
              </div>
            </div>

            {/* Audio Level Meter */}
            <div className="mt-3 md:mt-4 flex items-center gap-2 md:gap-3">
              <Volume2 className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
              <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#6a1fbf] to-[#00c853] transition-all duration-100"
                  style={{ width: `${audioLevel}%` }}
                />
              </div>
            </div>

          </div>
        </div>

        {/* Right Section - Scheduling Form */}
        <div className="lg:w-[500px] xl:w-[600px] bg-white/5 backdrop-blur-xl lg:border-l border-t lg:border-t-0 border-white/10 flex flex-col max-h-screen">
          {/* Header - Fixed */}
          <div className="p-4 md:p-6 border-b border-white/10">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center">
                <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
              </div>
              <div>
                <h2 className="text-sm md:text-base font-bold font-sans">
                  R-Link Studio
                </h2>
                <p className="text-xs md:text-sm text-gray-400 font-sans">
                  Configure your settings
                </p>
              </div>
            </div>
          </div>

          {/* Scrollable Form Content */}
          <div className="overflow-y-auto p-4 md:p-6 space-y-6">
            {/* Camera & Microphone Toggles */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex items-center gap-3">
                  {isCameraOn ? <Video className="w-4 h-4 text-white" /> : <VideoOff className="w-4 h-4 text-gray-400" />}
                  <span className="text-sm text-white font-sans">Camera</span>
                </div>
                <button
                  onClick={toggleCamera}
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    isCameraOn ? "border-red-500 bg-red-500/20" : "border-red-500 bg-red-500/20"
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full ${isCameraOn ? "bg-red-500" : "bg-red-500"}`} />
                </button>
              </div>
              <div className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex items-center gap-3">
                  {isMicOn ? <Mic className="w-4 h-4 text-white" /> : <MicOff className="w-4 h-4 text-gray-400" />}
                  <span className="text-sm text-white font-sans">Microphone</span>
                </div>
                <button
                  onClick={toggleMic}
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                    isMicOn ? "border-red-500 bg-red-500/20" : "border-red-500 bg-red-500/20"
                  }`}
                >
                  <div className={`w-2 h-2 rounded-full ${isMicOn ? "bg-red-500" : "bg-red-500"}`} />
                </button>
              </div>
            </div>

            {/* Device Selectors */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2 font-sans">Camera</label>
                <select
                  value={selectedDevices.camera}
                  onChange={(e) => handleCameraChange(e.target.value)}
                  className="w-full appearance-none p-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white outline-none focus:border-[#6a1fbf] transition-colors font-sans"
                >
                  <option value="" className="bg-[#1a1a2e]">Select camera</option>
                  {devices.cameras.map((device) => (
                    <option key={device.deviceId} value={device.deviceId} className="bg-[#1a1a2e]">
                      {device.label || "Camera"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2 font-sans">Microphone</label>
                <select
                  value={selectedDevices.microphone}
                  onChange={(e) => handleMicChange(e.target.value)}
                  className="w-full appearance-none p-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white outline-none focus:border-[#6a1fbf] transition-colors font-sans"
                >
                  <option value="" className="bg-[#1a1a2e]">Select microphone</option>
                  {devices.microphones.map((device) => (
                    <option key={device.deviceId} value={device.deviceId} className="bg-[#1a1a2e]">
                      {device.label || "Microphone"}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2 font-sans">
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#6a1fbf] transition-colors font-sans"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2 font-sans">
                  Event Name
                </label>
                <input
                  type="text"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  placeholder={`Enter ${sessionLabels[sessionType].toLowerCase()} name`}
                  maxLength={25}
                  className={`w-full p-3 bg-white/5 border rounded-xl text-sm text-white placeholder:text-gray-500 focus:outline-none transition-colors font-sans ${
                    eventName.trim() && eventName.trim().length < 3
                      ? "border-red-500 focus:border-red-500"
                      : "border-white/10 focus:border-[#6a1fbf]"
                  }`}
                />
              </div>
            </div>

            {/* Room Association (Meetings only - required) */}
            {sessionType === "meeting" && rooms.length > 0 && (
              <div className="mt-3">
                <label className="block text-sm text-gray-400 mb-2 font-sans">
                  Room
                </label>
                <select
                  value={selectedRoomId}
                  onChange={(e) => setSelectedRoomId(e.target.value)}
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#6a1fbf] transition-colors font-sans appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23fff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 1rem center",
                  }}
                >
                  {rooms.map((room) => (
                    <option key={room.id || room._id} value={room.id || room._id} style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
                      {room.roomName || room.name || "Unnamed Room"}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {permissions.camera === "denied" ||
            permissions.microphone === "denied" ? (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-sm text-red-300 font-sans">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  Please allow camera and microphone access in your browser
                  settings.
                </p>
              </div>
            ) : null}
          </div>


          {/* Action Button - Fixed at bottom */}
          <div className="p-4 md:p-6 border-t border-white/10">
            <button
              onClick={handleMeetNow}
              disabled={isStartingInline || isStartingMeetNow || isCreating}
              className="w-full py-3 text-sm font-semibold bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 transition-opacity disabled:opacity-50 rounded-xl flex items-center justify-center gap-2 font-sans"
            >
              {isStartingMeetNow ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  Enter Studio
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingSetup;
